/* Boot, screen routing and the sidebar.
   Ported from artifact.html: three screens toggled by a class, the sidebar as the only
   navigation, and the flow stepper that disappears once results exist. */

import { $, esc, fmt } from "./util.js";
import { paintIcons } from "./icons.js";
import { api } from "./api.js";
import { toast } from "./toast.js";
import { state, prefs } from "./state.js";
import { renderStepper } from "./components/stepper.js";
import { showOnboarding } from "./components/onboarding.js";
import { initAuthScreen, resetEmailStep } from "./screens/auth.js";
import { renderHome } from "./screens/home.js";
import { openProjectWorkspace, setTab } from "./screens/project.js";
import { initTheme } from "./theme.js";
import { projectBadge } from "./logos.js";

/* Sidebar entries for an open project. The search journey sits directly under Queries. */
const NAV_ITEMS = [
  ["queries", "table", "Queries"],
  ["journey", "route", "Search journey"],
  ["overview", "chart", "Charts"],
  ["topics", "layers", "Topics"],
  ["actions", "wrench", "SEO actions"],
  ["ref", "book", "Reference"],
];

let projectsWithResults = false;

/* ---------- routing ---------- */

export function showScreen(which) {
  state.screen = which;
  $("screen-auth").classList.toggle("hidden", which !== "auth");
  $("screen-home").classList.toggle("hidden", which !== "home");
  $("screen-project").classList.toggle("hidden", which !== "project");
  /* The landing page runs full width with no sidebar. */
  document.body.classList.toggle("landing", which === "auth");

  if (which === "auth") { renderStepper(1); paintIcons($("screen-auth")); }
  if (which === "home") {
    renderStepper(2);
    /* Once any project has results the stepper has done its job. */
    $("fs-home").classList.toggle("hidden", projectsWithResults);
  }
  if (which === "project") renderStepper(3);
  renderSide();
}

/* ---------- sidebar ---------- */

function renderSide() {
  if (!state.user) { $("sb-nav").innerHTML = ""; $("sb-proj").innerHTML = ""; return; }

  let html = '<div class="sb-item' + (state.screen === "home" ? " on" : "") + '" data-nav="home"><i data-ic="folder"></i>Projects</div>';
  if (state.currentProject && state.run) {
    const name = state.currentProject.name;
    html += '<div class="sb-sec">' + esc(name.length > 22 ? name.slice(0, 22) + "…" : name) + "</div>";
    html += NAV_ITEMS.map(([t, ic, label]) =>
      '<div class="sb-item' + (state.tab === t && state.screen === "project" ? " on" : "") + '" data-nav="' + t + '"><i data-ic="' + ic + '"></i>' + label + "</div>").join("");
  }
  $("sb-nav").innerHTML = html;
  paintIcons($("sb-nav"));
  $("sb-nav").querySelectorAll("[data-nav]").forEach(el => el.addEventListener("click", () => {
    if (el.dataset.nav === "home") { openHome(); return; }
    showScreen("project");
    setTab(el.dataset.nav);
  }));

  const guest = !!state.user.guest;
  let pjHtml = "";
  if (state.currentProject && state.run?.summary) {
    const s = state.run.summary;
    pjHtml += '<div class="sb-pj">' + esc(state.currentProject.name) +
      '<span class="m">' + fmt(s.totalQ) + " queries · " + fmt(s.conv) + " conversational</span></div>";
  }
  /* A guest is invited to sign up rather than sign out — their work carries over. */
  pjHtml += '<div class="sb-pj" style="display:flex;align-items:center;justify-content:space-between;gap:8px">' +
    '<span class="m" style="margin:0;overflow:hidden;text-overflow:ellipsis">' +
    (guest ? "Guest session" : esc(state.user.email)) + "</span>" +
    (guest
      ? '<button class="btn-x" id="btn-signup" style="padding:3px 8px" title="Sign up to keep your projects across devices">Sign up</button>'
      : '<button class="btn-x" id="btn-signout" style="padding:3px 8px">Sign out</button>') + "</div>";
  $("sb-proj").innerHTML = pjHtml;

  $("btn-signout")?.addEventListener("click", signOut);
  /* Keep the guest session alive so signing up can carry the projects over. */
  $("btn-signup")?.addEventListener("click", () => { showScreen("auth"); $("auth-email")?.focus(); });
}

/* ---------- screens ---------- */

async function openHome() {
  const projects = await renderHome({ onOpenProject: openProject });
  projectsWithResults = projects.some(p => p.summary);
  showScreen("home");
}

async function openProject(project) {
  showScreen("project");
  await openProjectWorkspace(project, { onRefreshSidebar: renderSide });
}

function onSignedIn(user, extra = {}) {
  state.user = user;
  resetEmailStep();
  if (extra.projectsKept > 0) toast(`Signed in — your ${extra.projectsKept === 1 ? "project" : "projects"} came with you`);
  openHome();
  if (!prefs.onboarded) showOnboarding(0);
}

async function signOut() {
  try { await api.signOut(); } catch { /* the session is going away regardless */ }
  state.user = null;
  state.currentProject = null;
  state.run = null;
  resetEmailStep();
  showScreen("auth");
}

/* ---------- boot ---------- */

/* One broken widget must never take the whole page down with it — that is how the landing
   page ended up invisible once already. Each piece of wiring is isolated. */
function safely(what, fn) {
  try { fn(); } catch (err) { console.error(`[oqc] could not set up ${what}:`, err); }
}

async function boot() {
  safely("icons", () => paintIcons());
  safely("theme", () => initTheme());
  safely("sign-in", () => initAuthScreen({ onSignedIn }));
  safely("help button", () => $("btn-help")?.addEventListener("click", () => showOnboarding(0)));

  /* Landing page: start without an account, or jump to the sign-in card. */
  $("btn-guest")?.addEventListener("click", async () => {
    try {
      const { user } = await api.startGuest();
      state.user = user;
      toast("Running as guest — create a project to begin");
      await openHome();
    } catch (err) {
      toast(err.message);
    }
  });
  $("lnav-signin")?.addEventListener("click", () => {
    $("authcard")?.scrollIntoView({ behavior: "smooth", block: "center" });
    $("auth-email")?.focus();
  });

  try {
    const me = await api.me();
    state.googleEnabled = me.googleEnabled;
    state.googleClientId = me.googleClientId;
    state.allowedDomains = me.allowedDomains || [];
    state.user = me.user;
    /* Say up front who this is for, rather than refusing people after they try. */
    if (state.allowedDomains.length) {
      const list = state.allowedDomains.join(" or ");
      const fine = document.querySelector("#screen-auth .afine");
      if (fine) fine.textContent = `Sign in with your ${list} account.`;
      const input = $("auth-email");
      if (input) input.placeholder = `you@${state.allowedDomains[0]}`;
    }
  } catch {
    toast("Could not reach the server — is it running?");
  }

  /* Decide the screen last, and always land on something: a failure here used to leave
     every screen in its default state, showing the wrong page. */
  try {
    if (state.user) {
      await openHome();
      if (!prefs.onboarded) showOnboarding(0);
    } else {
      showScreen("auth");
    }
  } catch (err) {
    console.error("[oqc] could not open the starting screen:", err);
    showScreen("auth");
  }
}

boot();
